import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import type { QuotationFormOtherCostRow } from '../quotationFormTypes';

export function emptyQuotationFormOtherCostRow(): QuotationFormOtherCostRow {
  return {
    key: `c-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    description: '',
    quantity: '1',
    cost_price: '',
  };
}

type Props = {
  open: boolean;
  onClose: () => void;
  initialRow: QuotationFormOtherCostRow | null;
  onSave: (row: QuotationFormOtherCostRow) => void;
};

function validateFields(description: string, quantity: string, cost_price: string): { ok: true } | { ok: false; message: string } {
  const d = description.trim();
  if (!d) {
    return { ok: false, message: 'Deskripsi wajib diisi.' };
  }
  const q = Number.parseInt(quantity.trim(), 10);
  if (!Number.isFinite(q) || q < 1) {
    return { ok: false, message: 'Kuantitas minimal 1.' };
  }
  const pTrim = cost_price.trim();
  const p = Number.parseFloat(pTrim.replace(',', '.'));
  if (!Number.isFinite(p) || p < 0) {
    return { ok: false, message: 'Harga satuan harus angka ≥ 0.' };
  }
  return { ok: true };
}

/** Modal tambah / ubah satu baris biaya lainnya. */
export function QuotationOtherCostFormDialog({ open, onClose, initialRow, onSave }: Props) {
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [cost_price, setCostPrice] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (initialRow) {
      setDescription(initialRow.description);
      setQuantity(initialRow.quantity.trim() || '1');
      setCostPrice(initialRow.cost_price);
    } else {
      setDescription('');
      setQuantity('1');
      setCostPrice('');
    }
  }, [open, initialRow]);

  function handleSubmit(): void {
    const v = validateFields(description, quantity, cost_price);
    if (!v.ok) {
      setError(v.message);
      return;
    }
    const pTrim = cost_price.trim();
    const p = Number.parseFloat(pTrim.replace(',', '.'));
    const base = initialRow ?? emptyQuotationFormOtherCostRow();
    onSave({
      ...base,
      description: description.trim(),
      quantity: quantity.trim(),
      cost_price: String(p),
    });
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="quotation-form-other-cost-dialog">
      <DialogTitle>{initialRow ? 'Ubah biaya lainnya' : 'Biaya lainnya baru'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label="Deskripsi"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size="small"
            required
            inputProps={{ 'data-testid': 'quotation-form-other-cost-dialog-desc' }}
          />
          <TextField
            label="Kuantitas"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ min: 1, step: 1, 'data-testid': 'quotation-form-other-cost-dialog-qty' }}
          />
          <TextField
            label="Harga satuan"
            type="number"
            value={cost_price}
            onChange={(e) => setCostPrice(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ min: 0, step: 'any', 'data-testid': 'quotation-form-other-cost-dialog-price' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Batal
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          data-testid="quotation-form-other-cost-dialog-save"
        >
          Simpan
        </Button>
      </DialogActions>
    </Dialog>
  );
}
